import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { IntakeTabValidationKey } from '../store/slices/intakeSlice';
import { useShallow } from 'zustand/react/shallow';
import { useIntake } from '../hooks/useIntake';
import { useSetSupportContext } from '../hooks/useSupportContext';
import { useIntakeDocuments } from '../hooks/useIntakeDocuments';
import { useIntakeFormSync } from '../hooks/useIntakeFormSync';
import { useToast } from '../hooks/useToastContext';
import { useIntakeTabValidationOnLoad } from '../hooks/useIntakeTabValidationOnLoad';
import type { AgencyInsuranceResponse } from '../services/api/types.gen';
import type { DocumentValidationManualOverrides } from '../types/intake.types';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  PanelLeftClose,
  PanelLeftOpen,
  MessageSquare,
  X,
  UserCircle2,
  Archive,
  ArchiveRestore,
  ExternalLink,
} from 'lucide-react';
import {
  type IntakeResponse,
  getIntakeDocumentsNameDict,
  updateIntakeDocument,
} from '../services/api';
import PDFViewerNative from '../components/PDFViewerNative';
import EligibilityCheckStep from '../components/intake/EligibilityCheckStep';
import SchedulingStep from '../components/intake/SchedulingStep';
import CreateEpisodeStep from '../components/intake/CreateEpisodeStep';
import PriorAuthStep from '../components/intake/PriorAuthStep';
import { ExtractedFields } from '../components/intake/ExtractedInfoStep';
import {
  AutoSaveComments,
  ChecklistItem,
  PatientNotes,
  InlineEditableField,
  InlineEditableDateField,
} from '../components/intake/shared';
import MarkCompletedButton from '../components/intake/MarkCompletedButton';
import { IntakeTestPatientToggle } from '../components/intake/IntakeTestPatientToggle';
import { TestPatientBadge } from '../components/patient/TestPatientBadge';
import { formatPhone } from '../utils/intake';
import { getDefaultLandingPath } from '../utils/defaultLandingPath';
import { AiExtractedInsuranceSuggestion } from '../components/intake/AiExtractedInsuranceSuggestion';
import { INTAKE_STATUS } from '../constants/intakeStatus';
import { FeatureFlag } from '../constants/featureFlags';
import { ORDER_TYPE_OPTIONS } from '../constants/orderTypes';
import WorkflowStepper, {
  type WorkflowStep,
} from '../components/intake/WorkflowStepper';
import type {
  CodingExtractionItem,
  ChecklistItemDetails,
} from '../types/codingData.types';
import { IntakeUserAssignmentDialog } from '../components/IntakeUserAssignmentDialog';
import { filterAgencyInsurancesByClinic } from '../utils/insuranceOptions';
import { scrollToField } from '../utils/scrollToField';
import { clinicParticipatesInKinnser } from '../utils/clinicKeyUtils';

const INTAKE_TAB_NAMES = {
  EXTRACTION: 'extraction',
  ELIGIBILITY: 'eligibility',
  PRIOR_AUTH: 'prior-auth',
  READY_TO_CALL: 'ready-to-call',
  SCHEDULING: 'scheduling',
  PUSH_TO_EMR: 'push-to-emr',
} as const;

type IntakeTabValue = (typeof INTAKE_TAB_NAMES)[keyof typeof INTAKE_TAB_NAMES];

const Intake3: React.FC = () => {
  const { intakeId } = useParams<{ intakeId: string }>();
  const navigate = useNavigate();
  const {
    hasPermission,
    hasAnyPermission,
    hasFeatureFlag,
    agencyInsurances,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
    shouldRefreshAgencyInsurances,
    agencyClinics,
    agencyClinicsLoading,
    fetchAgencyClinics,
    agencyInsurancesError,
    agencyClinicsError,
    user,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      agencyInsurances: state.agencyInsurances,
      agencyInsurancesLoading: state.agencyInsurancesLoading,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
      shouldRefreshAgencyInsurances: state.shouldRefreshAgencyInsurances,
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      fetchAgencyClinics: state.fetchAgencyClinics,
      agencyInsurancesError: state.agencyInsurancesError,
      agencyClinicsError: state.agencyClinicsError,
      user: state.user,
    }))
  );

  const hasOrderManagerFeatureFlag = hasFeatureFlag(FeatureFlag.ORDER_MANAGER);
  const hasPdfStampFeatureFlag = hasFeatureFlag(FeatureFlag.INTAKE_PDF_STAMP);
  const showAIExplanations = hasFeatureFlag(
    FeatureFlag.INTAKE_SEE_AI_EXPLANATION
  );

  // Use intakeSlice store
  const {
    selectedIntake,
    loadIntakeDetails,
    updateIntakeRecord,
    updateIntakeStatusRecord,
  } = useIntake();

  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);

  // Validate all tabs when intake loads so we know validity without opening each tab
  useIntakeTabValidationOnLoad(selectedIntake ?? null, agencyInsurances);

  // Document management hook
  const {
    documentUrls,
    activeDocumentId,
    setActiveDocumentId,
    handleDocumentRename,
    handleDocumentAdd,
    handleDocumentDelete,
    handleDocumentStamp,
    handleRefreshDocumentUrl,
    refreshDocuments,
  } = useIntakeDocuments({
    intakeId,
    documents: selectedIntake?.documents,
    loadIntakeDetails,
  });

  // Keep UI-specific state local
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<IntakeTabValue>(
    INTAKE_TAB_NAMES.EXTRACTION
  );
  const hasFetchedAgencyInsurancesRef = React.useRef(false);

  // formData synced from selectedIntake.
  // While on the Extraction tab, local formData is the source of truth
  // (DynamicIntakeForm owns edits); skip re-syncs to avoid clobbering.
  const [formData, setFormData] = useIntakeFormSync({
    selectedIntake,
    extract: intake => intake as unknown as Partial<IntakeResponse>,
    skipResync: activeTab === INTAKE_TAB_NAMES.EXTRACTION,
  });

  // Sticky PDF viewer logic
  const [isAssigneeDialogOpen, setIsAssigneeDialogOpen] = useState(false);
  const [documentNameDict, setDocumentNameDict] = useState<
    Record<string, string>
  >({});

  // Toast
  const { error: showToastError } = useToast();

  // Support context
  const patientFullName = [
    formData.patient_first_name,
    formData.patient_last_name,
  ]
    .filter(Boolean)
    .join(' ');
  useSetSupportContext({
    label: patientFullName ? (
      <>
        Intake for{' '}
        <span className='font-bold text-purple-600'>{patientFullName}</span>
      </>
    ) : (
      'Intake'
    ),
    subject: patientFullName
      ? `Intake request - ${patientFullName}`
      : 'Intake request',
    hiddenContext: {
      ...(intakeId && { intakeId }),
      ...(formData.status && { status: formData.status }),
    },
  });

  // Ready to Call has no form validations; valid for Push to EMR gate (don't require "mark complete").
  useEffect(() => {
    if (activeTab === INTAKE_TAB_NAMES.READY_TO_CALL) {
      setIntakeTabValid(IntakeTabValidationKey.ReadyToCall, true);
    }
  }, [activeTab, setIntakeTabValid]);

  // Refetch intake when switching to Push to EMR tab so status (e.g. PUSHED_TO_KINNSER) is current
  useEffect(() => {
    if (activeTab === INTAKE_TAB_NAMES.PUSH_TO_EMR && intakeId) {
      loadIntakeDetails(intakeId);
    }
  }, [activeTab, intakeId, loadIntakeDetails]);

  // Check permission - redirect if user doesn't have intake:access
  useEffect(() => {
    if (!hasPermission('intake:access')) {
      navigate(
        getDefaultLandingPath({
          hasPermission,
          hasAnyPermission,
          hasFeatureFlag,
        })
      );
    }
  }, [hasAnyPermission, hasFeatureFlag, hasPermission, navigate]);

  // Fetch agency insurances on component mount (use cached data if available and fresh)
  useEffect(() => {
    if (
      !hasFetchedAgencyInsurancesRef.current &&
      shouldRefreshAgencyInsurances() &&
      !agencyInsurancesLoading
    ) {
      hasFetchedAgencyInsurancesRef.current = true;
      fetchAgencyInsurances();
    }
  }, [
    shouldRefreshAgencyInsurances,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
  ]);

  // Fetch agency clinics on mount
  const hasFetchedAgencyClinicsRef = React.useRef(false);
  useEffect(() => {
    if (
      !hasFetchedAgencyClinicsRef.current &&
      agencyClinics.length === 0 &&
      !agencyClinicsLoading
    ) {
      hasFetchedAgencyClinicsRef.current = true;
      fetchAgencyClinics();
    }
  }, [agencyClinics.length, agencyClinicsLoading, fetchAgencyClinics]);

  useEffect(() => {
    const loadIntake = async () => {
      if (!intakeId) {
        setError('No intake ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const intake = await loadIntakeDetails(intakeId);

        if (intake) {
          // Convert IntakeRecord to IntakeResponse for form data
          setFormData(intake as unknown as Partial<IntakeResponse>);
        } else {
          setError('Intake not found');
        }
      } catch (err) {
        console.error('Error loading intake:', err);
        setError('Failed to load intake');
      } finally {
        setLoading(false);
      }
    };

    loadIntake();
  }, [intakeId, loadIntakeDetails]);

  // Load document name dict (upload name -> display name) for extraction tab.
  // Depend on stable document ids so we don't refetch on every selectedIntake update (e.g. scheduling save).
  const documentIdsKey =
    selectedIntake?.documents
      ?.map((d: { id?: string }) => d.id)
      .filter(Boolean)
      .sort()
      .join(',') ?? '';
  useEffect(() => {
    if (!intakeId) return;
    let cancelled = false;
    getIntakeDocumentsNameDict({
      path: { intake_id: intakeId },
    })
      .then(res => {
        if (cancelled) return;
        const data = res.data as { name_dict?: Record<string, string> };
        setDocumentNameDict(data?.name_dict ?? {});
      })
      .catch(() => {
        if (!cancelled) setDocumentNameDict({});
      });
    return () => {
      cancelled = true;
    };
  }, [intakeId, documentIdsKey]);

  // RogerDoc clinic key for patient duplicate check (match API expects this, not agency clinic id).
  // Only resolve once the intake has an agency clinic selected — otherwise we'd be running the
  // duplicate check against an unrelated clinic (e.g. agencyClinics[0]) and the gating UI flips
  // back off as soon as the real clinic resolves.
  const selectedClinicForMatch = formData.agency_clinic_id
    ? agencyClinics.find(c => c.id === formData.agency_clinic_id)
    : null;
  // Kinnser-participating clinics carry a WellSky clinic id. Roger-only clinics also have a
  // sequence-assigned clinic_key — exclude those via clinicParticipatesInKinnser (EMR-375).
  const clinicKeyForMatch = clinicParticipatesInKinnser(selectedClinicForMatch)
    ? (selectedClinicForMatch?.clinic_key ?? null)
    : null;

  // Construct agency insurance options
  const agencyInsuranceOptions = [
    {
      value: '',
      label: 'None',
    },
    ...(() => {
      const filteredInsurances: AgencyInsuranceResponse[] =
        filterAgencyInsurancesByClinic(
          agencyInsurances,
          formData.agency_clinic_id
        );

      // Deduplicate by name + edi_payer_id combination
      // Eligibility indicator prefix (check mark + two non-breaking spaces)
      const ELIGIBILITY_INDICATOR = '✓\u00A0\u00A0';

      const uniqueInsurances = new Map();
      filteredInsurances.forEach((agencyInsurance: AgencyInsuranceResponse) => {
        const key = `${agencyInsurance.name}_${agencyInsurance.edi_payer_id}`;
        if (!uniqueInsurances.has(key)) {
          // Check if eligibility is supported (has stedi_eligibility_enabled from linked payor)
          const supportsEligibility = Boolean(
            agencyInsurance.stedi_eligibility_enabled
          );
          uniqueInsurances.set(key, {
            value: agencyInsurance.id,
            label: `${supportsEligibility ? ELIGIBILITY_INDICATOR : ''}${agencyInsurance.name}${agencyInsurance.edi_payer_id ? ` (${agencyInsurance.edi_payer_id})` : ''}`,
          });
        }
      });

      // Sort: eligibility-supported first, then alphanumeric, then non-alphanumeric
      return Array.from(uniqueInsurances.values()).sort((a, b) => {
        // First sort by eligibility support (✓ comes first)
        const aSupportsElig = a.label.startsWith(ELIGIBILITY_INDICATOR);
        const bSupportsElig = b.label.startsWith(ELIGIBILITY_INDICATOR);
        if (aSupportsElig && !bSupportsElig) return -1;
        if (!aSupportsElig && bSupportsElig) return 1;

        // Then sort alphanumeric first, then non-alphanumeric
        // Strip the eligibility indicator prefix for comparison
        const aLabelClean = a.label.replace(ELIGIBILITY_INDICATOR, '');
        const bLabelClean = b.label.replace(ELIGIBILITY_INDICATOR, '');
        const aStartsWithAlphanum = /^[a-zA-Z0-9]/.test(aLabelClean);
        const bStartsWithAlphanum = /^[a-zA-Z0-9]/.test(bLabelClean);

        if (aStartsWithAlphanum && !bStartsWithAlphanum) return -1;
        if (!aStartsWithAlphanum && bStartsWithAlphanum) return 1;

        return aLabelClean.localeCompare(bLabelClean);
      });
    })(),
  ];

  // Replace each key in name_dict with its display value in text (e.g. filenames -> display names)
  const replaceKeysWithDisplayNames = (
    text: string | null | undefined,
    nameDict: Record<string, string>
  ): string | null | undefined => {
    if (text == null || text === '') return text;
    const keys = Object.keys(nameDict).filter(k => k.length > 0);
    let result = text;
    for (const key of keys) {
      result = result.split(key).join(nameDict[key]);
    }
    return result;
  };

  // Type for coding extraction from API
  interface CodingExtraction {
    face_to_face_visits?: CodingExtractionItem[];
    home_health_orders?: CodingExtractionItem[];
    authorized_diagnoses?: CodingExtractionItem[];
    not_qualifying_face_to_face_visits_reasoning?: string | null;
    not_qualifying_home_health_orders_reasoning?: string | null;
  }

  // Manual overrides for document validation badges (stored in intake fields, merged to root by API)
  const documentValidationOverrides: DocumentValidationManualOverrides =
    (
      selectedIntake as unknown as {
        document_validation_manual_overrides?: DocumentValidationManualOverrides;
      }
    )?.document_validation_manual_overrides ??
    (
      selectedIntake as unknown as {
        fields?: {
          document_validation_manual_overrides?: DocumentValidationManualOverrides;
        };
      }
    )?.fields?.document_validation_manual_overrides ??
    {};

  const formatManualOverrideMessage = (
    value: boolean,
    markedAt?: string,
    markedByEmail?: string
  ): string => {
    const timeStr =
      markedAt != null
        ? new Date(markedAt).toLocaleString(undefined, {
            timeZone: 'UTC',
            dateStyle: 'short',
            timeStyle: 'short',
          }) + ' UTC'
        : '';
    const byStr = markedByEmail ? ` by ${markedByEmail}` : '';
    return value
      ? `Manually marked as present${byStr}${timeStr ? ` at ${timeStr}` : ''}.`
      : `Manually marked as not present${byStr}${timeStr ? ` at ${timeStr}` : ''}.`;
  };

  // Calculate checklist statuses with details from coding extraction + manual overrides
  const getChecklistStatusesWithDetails = (): {
    statuses: Record<string, 'complete' | 'missing' | 'not-applicable'>;
    details: Record<string, ChecklistItemDetails>;
  } => {
    const codingExtraction = (
      selectedIntake as unknown as IntakeResponse & {
        coding_extraction?: CodingExtraction;
      }
    )?.coding_extraction;

    const homeHealthOrders = codingExtraction?.home_health_orders ?? [];
    const faceToFaceVisits = codingExtraction?.face_to_face_visits ?? [];
    const hasOrdersFromCoding = homeHealthOrders.length > 0;
    const hasF2FFromCoding = faceToFaceVisits.length > 0;
    const hasSignedOrder = homeHealthOrders.some(
      order => order.validations?.is_signed?.value === true
    );
    const hasSignedF2F = faceToFaceVisits.some(
      f2f => f2f.validations?.is_signed?.value === true
    );

    let orderAttachedStatus: 'complete' | 'missing' | 'not-applicable' =
      hasOrdersFromCoding ? 'complete' : 'missing';
    let orderSignedStatus: 'complete' | 'missing' | 'not-applicable' =
      hasSignedOrder ? 'complete' : 'missing';
    let f2fAttachedStatus: 'complete' | 'missing' | 'not-applicable' =
      hasF2FFromCoding ? 'complete' : 'missing';
    let f2fSignedStatus: 'complete' | 'missing' | 'not-applicable' =
      hasSignedF2F ? 'complete' : 'missing';
    let hAndPStatus: 'complete' | 'missing' | 'not-applicable' =
      formData.past_medical_history?.trim() ? 'complete' : 'missing';

    if (documentValidationOverrides.order_attached != null) {
      orderAttachedStatus = documentValidationOverrides.order_attached.value
        ? 'complete'
        : 'missing';
    }
    if (documentValidationOverrides.order_signed != null) {
      orderSignedStatus = documentValidationOverrides.order_signed.value
        ? 'complete'
        : 'missing';
    }
    if (documentValidationOverrides.f2f_attached != null) {
      f2fAttachedStatus = documentValidationOverrides.f2f_attached.value
        ? 'complete'
        : 'missing';
    }
    if (documentValidationOverrides.f2f_signed != null) {
      f2fSignedStatus = documentValidationOverrides.f2f_signed.value
        ? 'complete'
        : 'missing';
    }
    if (documentValidationOverrides.h_and_p != null) {
      hAndPStatus = documentValidationOverrides.h_and_p.value
        ? 'complete'
        : 'missing';
    }

    const overrideMessage = (
      key:
        | 'order_attached'
        | 'order_signed'
        | 'f2f_attached'
        | 'f2f_signed'
        | 'h_and_p'
    ) => {
      const o = documentValidationOverrides[key];
      if (!o) return undefined;
      return formatManualOverrideMessage(
        o.value,
        o.marked_at ?? undefined,
        o.marked_by_user_email ?? undefined
      );
    };

    // Show manual override message only when there's no positive AI content to show.
    // If the user unchecked then rechecked and extraction had found content, show the AI explanation again.
    const hasPositiveAiContent: Record<string, boolean> = {
      order_attached: hasOrdersFromCoding,
      order_signed: hasSignedOrder,
      f2f_attached: hasF2FFromCoding,
      f2f_signed: hasSignedF2F,
      h_and_p: Boolean(formData.past_medical_history?.trim()),
    };
    const shouldShowManualMessage = (
      key:
        | 'order_attached'
        | 'order_signed'
        | 'f2f_attached'
        | 'f2f_signed'
        | 'h_and_p'
    ): boolean => {
      const o = documentValidationOverrides[key];
      if (!o) return false;
      if (!o.value) return true; // Manually marked not present: always show manual message
      return !hasPositiveAiContent[key]; // Manually marked present: show manual message only when no AI content
    };

    const details: Record<string, ChecklistItemDetails> = {
      orderAttached: {
        items: homeHealthOrders,
        notQualifyingReasoning: replaceKeysWithDisplayNames(
          codingExtraction?.not_qualifying_home_health_orders_reasoning,
          documentNameDict
        ),
        manualOverrideMessage: shouldShowManualMessage('order_attached')
          ? (overrideMessage('order_attached') ?? undefined)
          : undefined,
      },
      orderSigned: {
        items: homeHealthOrders,
        showOnlySignedItems: true,
        notQualifyingReasoning: !hasOrdersFromCoding
          ? replaceKeysWithDisplayNames(
              codingExtraction?.not_qualifying_home_health_orders_reasoning,
              documentNameDict
            )
          : undefined,
        manualOverrideMessage: shouldShowManualMessage('order_signed')
          ? (overrideMessage('order_signed') ?? undefined)
          : undefined,
      },
      f2fAttached: {
        items: faceToFaceVisits,
        notQualifyingReasoning: replaceKeysWithDisplayNames(
          codingExtraction?.not_qualifying_face_to_face_visits_reasoning,
          documentNameDict
        ),
        manualOverrideMessage: shouldShowManualMessage('f2f_attached')
          ? (overrideMessage('f2f_attached') ?? undefined)
          : undefined,
      },
      f2fSigned: {
        items: faceToFaceVisits,
        showOnlySignedItems: true,
        notQualifyingReasoning: !hasF2FFromCoding
          ? replaceKeysWithDisplayNames(
              codingExtraction?.not_qualifying_face_to_face_visits_reasoning,
              documentNameDict
            )
          : undefined,
        manualOverrideMessage: shouldShowManualMessage('f2f_signed')
          ? (overrideMessage('f2f_signed') ?? undefined)
          : undefined,
      },
      hAndP: {
        items: [],
        manualOverrideMessage: shouldShowManualMessage('h_and_p')
          ? (overrideMessage('h_and_p') ?? undefined)
          : undefined,
      },
    };

    return {
      statuses: {
        orderSigned: orderSignedStatus,
        orderAttached: orderAttachedStatus,
        f2fSigned: f2fSignedStatus,
        f2fAttached: f2fAttachedStatus,
        hAndP: hAndPStatus,
      },
      details,
    };
  };

  const { statuses: checklistStatuses, details: checklistDetails } =
    getChecklistStatusesWithDetails();

  const statusKeyByOverrideKey: Record<
    string,
    'orderAttached' | 'orderSigned' | 'f2fAttached' | 'f2fSigned' | 'hAndP'
  > = {
    order_attached: 'orderAttached',
    order_signed: 'orderSigned',
    f2f_attached: 'f2fAttached',
    f2f_signed: 'f2fSigned',
    h_and_p: 'hAndP',
  };

  const handleToggleDocumentValidation = useCallback(
    async (
      overrideKey:
        | 'order_attached'
        | 'order_signed'
        | 'f2f_attached'
        | 'f2f_signed'
        | 'h_and_p'
    ) => {
      if (!intakeId) return;
      const statusKey = statusKeyByOverrideKey[overrideKey];
      const currentStatus = checklistStatuses[statusKey];
      const newValue = currentStatus === 'complete' ? false : true;
      const newOverrides = {
        ...documentValidationOverrides,
        [overrideKey]: {
          value: newValue,
          marked_at: new Date().toISOString(),
          marked_by_user_id: user?.id ?? null,
          marked_by_user_email: user?.email ?? undefined,
        },
      };
      try {
        await updateIntakeRecord(intakeId, {
          document_validation_manual_overrides: newOverrides,
        });
      } catch (err) {
        console.error('Failed to update document validation override:', err);
        showToastError('Failed to update. Please try again.');
      }
    },
    [
      intakeId,
      checklistStatuses,
      documentValidationOverrides,
      user?.id,
      user?.email,
      updateIntakeRecord,
      showToastError,
    ]
  );

  if (loading) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  if (error || !selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-600 mb-4'>{error || 'Intake not found'}</p>
          <Button onClick={() => navigate('/intake-manager')}>
            Back to Intake Manager
          </Button>
        </div>
      </div>
    );
  }

  // Helper function to check if eligibility check tab is accessible
  // Requires extraction tab to be complete
  const canAccessEligibilityCheck = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if create episode tab is accessible
  // Requires extraction tab to be complete
  const canAccessCreateEpisode = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if prior auth tab is accessible
  // Requires extraction tab to be complete
  const canAccessPriorAuth = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if ready to call tab is accessible
  // Requires extraction tab to be complete
  const canAccessReadyToCall = () => {
    return isTabComplete('extraction');
  };

  // Helper function to check if scheduling tab is accessible
  // Requires extraction tab to be complete
  const canAccessScheduling = () => {
    return isTabComplete('extraction');
  };

  // Tab completion check - uses tab_statuses field
  const isTabComplete = (tabKey: string): boolean => {
    const tabStatus =
      selectedIntake?.tab_statuses?.[
        tabKey as keyof typeof selectedIntake.tab_statuses
      ];
    return tabStatus === 'complete' || tabStatus === 'complete_with_errors';
  };

  // Handler for saving patient notes
  const handleSaveNotes = async (notes: string) => {
    try {
      await updateIntakeRecord(intakeId!, { patient_notes: notes });
      setFormData(prev => ({ ...prev, patient_notes: notes }));
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  // Helper function to validate and set active tab
  const handleTabChange = (value: string) => {
    if (
      value === INTAKE_TAB_NAMES.EXTRACTION ||
      value === INTAKE_TAB_NAMES.ELIGIBILITY ||
      value === INTAKE_TAB_NAMES.PRIOR_AUTH ||
      value === INTAKE_TAB_NAMES.READY_TO_CALL ||
      value === INTAKE_TAB_NAMES.SCHEDULING ||
      value === INTAKE_TAB_NAMES.PUSH_TO_EMR
    ) {
      setActiveTab(value as IntakeTabValue);
    }
  };

  /** Scroll to a field on the current tab (Push to EMR content must be mounted). */
  const handleScrollToField = (fieldName: string): void => {
    scrollToField(fieldName);
  };

  // Handler for when extraction tab status changes (called by MarkCompletedButton)
  const handleExtractionStatusChange = async () => {
    // Store the current extraction status before the API call updates it
    const wasComplete =
      selectedIntake?.tab_statuses?.extraction === 'complete' ||
      selectedIntake?.tab_statuses?.extraction === 'complete_with_errors';

    // Reload intake from API to get the updated state after MarkCompletedButton's API call
    if (intakeId) {
      await loadIntakeDetails(intakeId);
    }

    // Only move to the next tab if extraction was just marked as complete (wasn't complete before)
    if (!wasComplete) {
      setActiveTab(INTAKE_TAB_NAMES.ELIGIBILITY);
    }
  };

  return (
    <div className='bg-background flex flex-col'>
      {/* Header */}
      <div className='border-b bg-background flex-shrink-0'>
        {/* Back Button Bar */}
        <div className='px-6 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => navigate('/intake-manager')}
            className='flex items-center gap-1.5'
          >
            <ArrowLeft className='h-3.5 w-3.5' />
            Back to Intake Manager
          </Button>
        </div>

        {/* Patient Info Row */}
        <div className='px-6 py-3'>
          <div className='flex items-start justify-between gap-4'>
            <div className='flex-1'>
              <div className='flex items-center gap-3 mb-2'>
                <h1 className='text-2xl font-bold text-gray-900'>
                  {formData.patient_first_name} {formData.patient_last_name}
                </h1>
                <PatientNotes
                  notes={formData.patient_notes || ''}
                  patientName={`${formData.patient_first_name} ${formData.patient_last_name}`}
                  phoneNumber={formData.patient_phone || 'N/A'}
                  onSave={handleSaveNotes}
                />
                <div className='group relative'>
                  <button
                    onClick={() => setIsAssigneeDialogOpen(true)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-all ${
                      selectedIntake.assigned_to_user
                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                    }`}
                    title={
                      selectedIntake.assigned_to_user
                        ? 'Click to reassign'
                        : 'Click to assign'
                    }
                  >
                    <UserCircle2 className='h-3.5 w-3.5' />
                    <span>
                      {selectedIntake.assigned_to_user?.email || 'Unassigned'}
                    </span>
                  </button>
                </div>
                {/* Read-only status indicator. The editable control lives in the
                    Extraction step (permission-gated) so it stays out of the way
                    of coordinators who don't manage it. */}
                {selectedIntake.is_test_intake && (
                  <TestPatientBadge className='shrink-0' />
                )}
              </div>
              <div className='text-sm text-muted-foreground flex flex-wrap items-center gap-x-1 gap-y-0.5'>
                <InlineEditableDateField
                  label='DOB'
                  value={formData.patient_dob}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, patient_dob: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      patient_dob: value,
                    });
                  }}
                  showAge
                  yearRange={{
                    fromYear: new Date().getFullYear() - 120,
                    toYear: new Date().getFullYear(),
                  }}
                />
                <span>•</span>
                <InlineEditableField
                  label='Insurance'
                  value={formData.agency_insurance?.name || ''}
                  disabled={true}
                  onChange={() => {}}
                  onBlur={() => {}}
                  placeholder='No Insurance'
                />
                <span>•</span>
                <InlineEditableField
                  label='Phone'
                  value={formData.patient_phone || ''}
                  format={formatPhone}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, patient_phone: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      patient_phone: value,
                    });
                  }}
                />
                {formData.patient_secondary_phone != null &&
                  formData.patient_secondary_phone !== '' && (
                    <>
                      <span>•</span>
                      <InlineEditableField
                        label='Secondary Phone'
                        value={formData.patient_secondary_phone}
                        format={formatPhone}
                        onChange={value => {
                          setFormData(prev => ({
                            ...prev,
                            patient_secondary_phone: value,
                          }));
                        }}
                        onBlur={value => {
                          updateIntakeRecord(intakeId!, {
                            patient_secondary_phone: value,
                          });
                        }}
                      />
                    </>
                  )}
                <span>•</span>
                <InlineEditableDateField
                  label='Referral'
                  value={formData.referral_date}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, referral_date: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      referral_date: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='Order'
                  value={formData.physician_order_date}
                  onChange={value => {
                    setFormData(prev => ({
                      ...prev,
                      physician_order_date: value,
                    }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      physician_order_date: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='Discharge'
                  value={formData.discharge_date}
                  onChange={value => {
                    setFormData(prev => ({ ...prev, discharge_date: value }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      discharge_date: value,
                    });
                  }}
                />
                <span>•</span>
                <InlineEditableDateField
                  label='F2F'
                  value={formData.f2f_encounter_date}
                  onChange={value => {
                    setFormData(prev => ({
                      ...prev,
                      f2f_encounter_date: value,
                    }));
                  }}
                  onBlur={value => {
                    updateIntakeRecord(intakeId!, {
                      f2f_encounter_date: value,
                    });
                  }}
                />
              </div>
            </div>
            <div className='flex flex-col items-end gap-2'>
              <div
                className={`inline-flex items-center rounded-full border border-transparent font-semibold text-xs px-3 py-1 flex-shrink-0 ${
                  selectedIntake.status === INTAKE_STATUS.PENDING_REVIEW
                    ? 'bg-orange-100 text-orange-800'
                    : selectedIntake.status ===
                          INTAKE_STATUS.PUSHED_TO_KINNSER ||
                        selectedIntake.status === INTAKE_STATUS.PUSHED_TO_ROGER
                      ? 'bg-green-700 text-white border-green-900'
                      : [
                            INTAKE_STATUS.ACCEPTED,
                            INTAKE_STATUS.SCHEDULED,
                          ].includes(selectedIntake.status)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }`}
              >
                {selectedIntake.status === INTAKE_STATUS.PENDING_REVIEW
                  ? 'Pending Review'
                  : selectedIntake.status === INTAKE_STATUS.ACCEPTED
                    ? 'Accepted'
                    : selectedIntake.status === INTAKE_STATUS.REJECTED
                      ? 'Not Admitted'
                      : selectedIntake.status
                          ?.split('_')
                          .map(
                            word =>
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                          )
                          .join(' ') || 'Unknown'}
              </div>
              {selectedIntake.status === INTAKE_STATUS.PUSHED_TO_ROGER &&
                selectedIntake.patient_id && (
                  <a
                    href={`/patients/${selectedIntake.patient_id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-900 underline'
                  >
                    View Patient
                    <ExternalLink className='h-3 w-3' />
                  </a>
                )}
              {!selectedIntake.non_admit && (
                <Button
                  size='default'
                  variant='outline'
                  disabled={isUpdatingStatus}
                  onClick={async () => {
                    setIsUpdatingStatus(true);
                    try {
                      await updateIntakeRecord(intakeId!, { non_admit: true });
                      await updateIntakeStatusRecord(
                        intakeId!,
                        INTAKE_STATUS.REJECTED
                      );
                      await loadIntakeDetails(intakeId!);
                      setFormData(prev => ({
                        ...prev,
                        non_admit: true,
                        status: INTAKE_STATUS.REJECTED,
                      }));
                    } catch (error) {
                      console.error('Error marking non-admit:', error);
                      showToastError(
                        'Failed to mark non-admit. Please try again.'
                      );
                    } finally {
                      setIsUpdatingStatus(false);
                    }
                  }}
                >
                  {isUpdatingStatus ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <X className='h-4 w-4 text-red-600' />
                  )}
                  Non-admit
                </Button>
              )}
              {selectedIntake.non_admit && (
                <Button
                  size='default'
                  variant='outline'
                  disabled={isUpdatingStatus}
                  onClick={async () => {
                    setIsUpdatingStatus(true);
                    try {
                      await updateIntakeRecord(intakeId!, { non_admit: false });
                      await updateIntakeStatusRecord(
                        intakeId!,
                        INTAKE_STATUS.PENDING_REVIEW
                      );
                      await loadIntakeDetails(intakeId!);
                      setFormData(prev => ({
                        ...prev,
                        non_admit: false,
                        status: INTAKE_STATUS.PENDING_REVIEW,
                      }));
                    } catch (error) {
                      console.error('Error undoing non-admit:', error);
                      showToastError(
                        'Failed to undo non-admit. Please try again.'
                      );
                    } finally {
                      setIsUpdatingStatus(false);
                    }
                  }}
                >
                  {isUpdatingStatus && (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  )}
                  Undo Non-admit
                </Button>
              )}
              {!selectedIntake.archived && (
                <Button
                  size='default'
                  variant='outline'
                  disabled={isUpdatingStatus}
                  onClick={async () => {
                    setIsUpdatingStatus(true);
                    try {
                      await updateIntakeRecord(intakeId!, { archived: true });
                      await loadIntakeDetails(intakeId!);
                      setFormData(prev => ({ ...prev, archived: true }));
                    } catch (error) {
                      console.error('Error archiving intake:', error);
                      showToastError('Failed to archive. Please try again.');
                    } finally {
                      setIsUpdatingStatus(false);
                    }
                  }}
                >
                  {isUpdatingStatus ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <Archive className='h-4 w-4 text-orange-600' />
                  )}
                  Archive
                </Button>
              )}
              {selectedIntake.archived && (
                <Button
                  size='default'
                  variant='outline'
                  disabled={isUpdatingStatus}
                  onClick={async () => {
                    setIsUpdatingStatus(true);
                    try {
                      await updateIntakeRecord(intakeId!, { archived: false });
                      await loadIntakeDetails(intakeId!);
                      setFormData(prev => ({ ...prev, archived: false }));
                    } catch (error) {
                      console.error('Error unarchiving intake:', error);
                      showToastError('Failed to unarchive. Please try again.');
                    } finally {
                      setIsUpdatingStatus(false);
                    }
                  }}
                >
                  {isUpdatingStatus ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <ArchiveRestore className='h-4 w-4 text-green-600' />
                  )}
                  Unarchive
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Validation Checklist Row */}
        <div className='px-6 pb-3 pt-2 border-t border-gray-100'>
          <div className='flex items-center gap-2 mb-2'>
            <span className='text-[10px] font-semibold text-gray-500 uppercase tracking-wide'>
              Document Validation
            </span>
            <div className='flex-1 h-px bg-gray-200'></div>
          </div>
          <div className='flex flex-wrap gap-2'>
            <ChecklistItem
              label='Order Attached'
              status={checklistStatuses.orderAttached}
              details={checklistDetails.orderAttached}
              onToggle={() => handleToggleDocumentValidation('order_attached')}
            />
            <ChecklistItem
              label='Order Signed'
              status={checklistStatuses.orderSigned}
              details={checklistDetails.orderSigned}
              onToggle={() => handleToggleDocumentValidation('order_signed')}
            />
            <ChecklistItem
              label='F2F Attached'
              status={checklistStatuses.f2fAttached}
              details={checklistDetails.f2fAttached}
              onToggle={() => handleToggleDocumentValidation('f2f_attached')}
            />
            <ChecklistItem
              label='F2F Signed'
              status={checklistStatuses.f2fSigned}
              details={checklistDetails.f2fSigned}
              onToggle={() => handleToggleDocumentValidation('f2f_signed')}
            />
            <ChecklistItem
              label='H&P'
              status={checklistStatuses.hAndP}
              details={checklistDetails.hAndP}
              onToggle={() => handleToggleDocumentValidation('h_and_p')}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 flex gap-4 p-4'>
        {/* Documents Section */}
        {!isFormExpanded && (
          <div className='w-1/2 sticky -top-4 self-start h-[calc(100vh-5rem)]'>
            <div className='h-full'>
              <PDFViewerNative
                documents={(selectedIntake.documents || []).map(doc => ({
                  id: doc.id,
                  fileName: doc.file_name,
                  mimeType: doc.mime_type ?? null,
                  isPersisted: true,
                  usedInExtraction: doc.used_in_extraction ?? true,
                  orderTypeTag: doc.task_type_tag,
                  stampedAt: doc.stamped_at,
                }))}
                documentUrls={documentUrls}
                activeDocumentId={activeDocumentId}
                onActiveDocumentChange={setActiveDocumentId}
                onDocumentRename={handleDocumentRename}
                onDocumentDelete={handleDocumentDelete}
                onDocumentAdd={handleDocumentAdd}
                onDocumentStamp={
                  hasPdfStampFeatureFlag ? handleDocumentStamp : undefined
                }
                onRefreshUrl={handleRefreshDocumentUrl}
                {...(hasOrderManagerFeatureFlag && {
                  orderTypeOptions: ORDER_TYPE_OPTIONS,
                  onOrderTypeChange: (docId: string, tag: string) => {
                    // Fire-and-forget — UI updates optimistically via localDocuments sync
                    updateIntakeDocument({
                      path: { document_id: docId },
                      body: { task_type_tag: tag || '' },
                    }).catch(err => {
                      console.error('Failed to update document tag', err);
                      showToastError(
                        'Failed to update order type. Please try again.'
                      );
                    });
                  },
                })}
              />
            </div>
          </div>
        )}

        {/* Extracted Information */}
        {/* Bound to the viewport height (mirrors the document pane) so the tab
            cards' h-full + internal overflow engage and each form scrolls
            inside its own card rather than scrolling the whole page. */}
        <div
          className={`${isFormExpanded ? 'w-full' : 'w-1/2'} flex flex-col h-[calc(100vh-5rem)] min-h-0`}
        >
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className='flex flex-col h-full'
          >
            {/* Workflow Navigation Header */}
            <div className='flex items-center gap-2 mb-4'>
              {/* PDF Toggle - compact icon button */}
              <button
                onClick={() => setIsFormExpanded(!isFormExpanded)}
                title={isFormExpanded ? 'Show PDF viewer' : 'Hide PDF viewer'}
                className='flex-shrink-0 p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors'
              >
                {isFormExpanded ? (
                  <PanelLeftOpen className='h-4 w-4' />
                ) : (
                  <PanelLeftClose className='h-4 w-4' />
                )}
              </button>

              <div className='flex-1 min-w-0'>
                <WorkflowStepper
                  steps={
                    [
                      {
                        id: INTAKE_TAB_NAMES.EXTRACTION,
                        label: 'Extraction',
                        shortLabel: 'Extraction',
                        isComplete: isTabComplete('extraction'),
                        isAccessible: true,
                      },
                      {
                        id: INTAKE_TAB_NAMES.ELIGIBILITY,
                        label: 'Eligibility',
                        shortLabel: 'Eligibility',
                        isComplete: isTabComplete('eligibility'),
                        isAccessible: canAccessEligibilityCheck(),
                      },
                      {
                        id: INTAKE_TAB_NAMES.PRIOR_AUTH,
                        label: 'Prior Authorization',
                        shortLabel: 'Prior Auth',
                        isComplete: isTabComplete('prior_auth'),
                        isAccessible: canAccessPriorAuth(),
                      },
                      {
                        id: INTAKE_TAB_NAMES.READY_TO_CALL,
                        label: 'Ready to Call',
                        shortLabel: 'Ready to Call',
                        isComplete: isTabComplete('ready_to_call'),
                        isAccessible: canAccessReadyToCall(),
                      },
                      {
                        id: INTAKE_TAB_NAMES.SCHEDULING,
                        label: 'Scheduling',
                        shortLabel: 'Scheduling',
                        isComplete: isTabComplete('scheduling'),
                        isAccessible: canAccessScheduling(),
                      },
                      {
                        id: INTAKE_TAB_NAMES.PUSH_TO_EMR,
                        label: 'Push to EMR',
                        shortLabel: 'Push to EMR',
                        isComplete: isTabComplete('push_to_emr'),
                        isAccessible: canAccessCreateEpisode(),
                      },
                    ] as WorkflowStep[]
                  }
                  activeStepId={activeTab}
                  onStepClick={stepId => handleTabChange(stepId)}
                />
              </div>
            </div>

            {/* Hidden TabsList for accessibility - actual navigation handled by stepper */}
            <TabsList className='hidden'>
              <TabsTrigger value={INTAKE_TAB_NAMES.EXTRACTION}>
                Extract
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.ELIGIBILITY}>
                Eligibility
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.PRIOR_AUTH}>
                Prior Auth
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.READY_TO_CALL}>
                Call
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.SCHEDULING}>
                Schedule
              </TabsTrigger>
              <TabsTrigger value={INTAKE_TAB_NAMES.PUSH_TO_EMR}>
                Push to EMR
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value={INTAKE_TAB_NAMES.EXTRACTION}
              className='flex-1 overflow-hidden'
            >
              <div className='h-full flex flex-col'>
                {/* Permission-gated test/practice marker. Rendered outside the
                    schema-driven form (which has no per-field permission concept)
                    but within the Extraction step — the first step, so the
                    eligibility/claim suppression takes effect before any benefits
                    check runs. Self-gates to null without `intake:access`.
                    Only shown for RogerEMR agencies (test-patient billing
                    suppression is a RogerEMR concern). */}
                {user?.agency?.is_rogeremr_enabled === true && (
                  <IntakeTestPatientToggle
                    intakeId={intakeId!}
                    isTest={selectedIntake.is_test_intake ?? false}
                    onChanged={async () => {
                      await loadIntakeDetails(intakeId!);
                    }}
                    className='shrink-0 border-b bg-muted/30 px-4 py-2.5'
                  />
                )}
                <div className='flex-1 overflow-hidden'>
                  <ExtractedFields
                    intake={selectedIntake as unknown as IntakeResponse}
                    intakeId={intakeId!}
                    formData={formData}
                    setFormData={setFormData}
                    onConfirm={handleExtractionStatusChange}
                    onIntakeUpdate={() => loadIntakeDetails(intakeId!)}
                    agencyInsuranceOptions={agencyInsuranceOptions}
                    agencyInsurancesLoading={agencyInsurancesLoading}
                    agencyInsurancesError={agencyInsurancesError}
                    agencyClinics={agencyClinics}
                    agencyClinicsLoading={agencyClinicsLoading}
                    agencyClinicsError={agencyClinicsError}
                    documentNameDict={documentNameDict}
                    clinicKeyForMatch={clinicKeyForMatch}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value={INTAKE_TAB_NAMES.ELIGIBILITY}
              className='flex-1 overflow-hidden'
            >
              {canAccessEligibilityCheck() ? (
                <EligibilityCheckStep
                  selectedIntake={selectedIntake}
                  onRefresh={async () => {
                    await loadIntakeDetails(intakeId!);
                  }}
                  onStatusChange={async () => {
                    await loadIntakeDetails(intakeId!);
                  }}
                  refreshDocuments={refreshDocuments}
                  agencyInsuranceOptions={agencyInsuranceOptions}
                  agencyInsurancesLoading={agencyInsurancesLoading}
                  agencyInsurancesError={agencyInsurancesError}
                  documentNameDict={documentNameDict}
                />
              ) : (
                <Card className='h-full flex items-center justify-center'>
                  <CardContent className='text-center'>
                    <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                    <p className='text-sm font-medium text-gray-900 mb-1'>
                      Confirmation Required
                    </p>
                    <p className='text-xs text-gray-500'>
                      Complete and confirm the extracted information first
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent
              value={INTAKE_TAB_NAMES.PRIOR_AUTH}
              className='flex-1 overflow-hidden'
            >
              {canAccessPriorAuth() ? (
                <PriorAuthStep
                  selectedIntake={selectedIntake}
                  agencyInsurances={agencyInsurances}
                  agencyInsurancesLoading={agencyInsurancesLoading}
                  onStatusChange={async () => {
                    await loadIntakeDetails(intakeId!);
                  }}
                />
              ) : (
                <Card className='h-full flex items-center justify-center'>
                  <CardContent className='text-center'>
                    <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                    <p className='text-sm font-medium text-gray-900 mb-1'>
                      Confirmation Required
                    </p>
                    <p className='text-xs text-gray-500'>
                      Complete eligibility check first
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent
              value={INTAKE_TAB_NAMES.READY_TO_CALL}
              className='flex-1 overflow-hidden'
            >
              {canAccessReadyToCall() ? (
                <Card className='h-full flex flex-col'>
                  <CardHeader className='border-b py-3 px-4'>
                    <div className='flex items-center justify-between'>
                      <div />
                      <MarkCompletedButton
                        intakeId={intakeId!}
                        tabKey='ready_to_call'
                        isComplete={isTabComplete('ready_to_call')}
                        onStatusChange={async () => {
                          await loadIntakeDetails(intakeId!);
                        }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
                    <div className='space-y-6'>
                      {/* Patient Contact Information */}
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                        <div className='border px-4 py-3'>
                          <div className='font-semibold mb-1'>
                            Contact Information
                          </div>
                          <div className='text-sm space-y-1'>
                            <div>
                              <span className='font-medium'>Patient:</span>{' '}
                              {selectedIntake.patient_first_name}{' '}
                              {selectedIntake.patient_last_name}
                            </div>
                            <div>
                              <span className='font-medium'>Phone:</span>{' '}
                              {selectedIntake.patient_phone || (
                                <span className='text-orange-700'>
                                  Not provided
                                </span>
                              )}
                            </div>
                            {selectedIntake.patient_email && (
                              <div>
                                <span className='font-medium'>Email:</span>{' '}
                                {selectedIntake.patient_email}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className='border px-4 py-3'>
                          <div className='font-semibold mb-1'>Address</div>
                          <div className='text-sm space-y-1'>
                            {selectedIntake.patient_street_address ? (
                              <>
                                <div>
                                  {selectedIntake.patient_street_address}
                                </div>
                                <div>
                                  {[
                                    selectedIntake.patient_city,
                                    selectedIntake.patient_state,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  {selectedIntake.patient_zip_code &&
                                    ` ${selectedIntake.patient_zip_code}`}
                                </div>
                              </>
                            ) : (
                              <div className='text-orange-700'>
                                No address provided
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Insurance Information */}
                      <div className='border px-4 py-3'>
                        <div className='font-semibold mb-2'>Insurance</div>
                        <div className='space-y-2'>
                          <VirtualizedFormSelect
                            label='Agency Insurance'
                            placeholder={
                              agencyInsurancesLoading
                                ? 'Loading agency insurances...'
                                : agencyInsurancesError
                                  ? 'Error loading agency insurances'
                                  : 'Select agency insurance'
                            }
                            options={agencyInsuranceOptions}
                            disabled={agencyInsurancesLoading}
                            name='agency_insurance_id'
                            defaultValue={
                              selectedIntake.agency_insurance_id || ''
                            }
                            onChange={async e => {
                              const newValue = e.target.value;
                              // updateIntakeRecord refreshes selectedIntake with the
                              // server's hydrated response (including the nested
                              // agency_insurance object), and useIntakeFormSync then
                              // resyncs formData -- which is what the patient-summary
                              // header reads (formData.agency_insurance?.name).
                              // Patching only agency_insurance_id here left that nested
                              // name stale until a page reload (ROG-3018), so let the
                              // resync be the single source of truth.
                              await updateIntakeRecord(intakeId!, {
                                agency_insurance_id: newValue || null,
                              });
                            }}
                          />
                          <div className='text-xs text-gray-500'>
                            ✓ = supports eligibility checks
                          </div>
                          <AiExtractedInsuranceSuggestion
                            value={selectedIntake.primary_insurance}
                            showAIExplanations={showAIExplanations}
                            fieldExplanation={
                              selectedIntake.fields_explanations
                                ?.primary_insurance
                            }
                            fieldsExplanations={
                              selectedIntake.fields_explanations
                            }
                            documentNameDict={documentNameDict}
                          />
                          {selectedIntake.primary_member_id && (
                            <div className='text-sm'>
                              <span className='font-medium'>Member ID:</span>{' '}
                              {selectedIntake.primary_member_id}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Comments Section */}
                      <div className='border px-4 py-3'>
                        <div className='flex items-center gap-2 mb-3'>
                          <MessageSquare className='h-4 w-4 text-muted-foreground' />
                          <div className='font-semibold'>Comments</div>
                        </div>
                        <AutoSaveComments
                          value={selectedIntake.comments || ''}
                          onSave={async (newComments: string) => {
                            await updateIntakeRecord(intakeId!, {
                              comments: newComments,
                            });
                            setFormData(prev => ({
                              ...prev,
                              comments: newComments,
                            }));
                          }}
                          placeholder='Add notes or comments about this intake...'
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className='h-full flex items-center justify-center'>
                  <CardContent className='text-center'>
                    <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                    <p className='text-sm font-medium text-gray-900 mb-1'>
                      Confirmation Required
                    </p>
                    <p className='text-xs text-gray-500'>
                      Complete and confirm the extracted information first
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent
              value={INTAKE_TAB_NAMES.SCHEDULING}
              className='flex-1 overflow-hidden'
            >
              {canAccessScheduling() ? (
                <SchedulingStep
                  onStatusChange={async () => {
                    await loadIntakeDetails(intakeId!);
                  }}
                  onScrollToField={handleScrollToField}
                />
              ) : (
                <Card className='h-full flex items-center justify-center'>
                  <CardContent className='text-center'>
                    <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                    <p className='text-sm font-medium text-gray-900 mb-1'>
                      Confirmation Required
                    </p>
                    <p className='text-xs text-gray-500'>
                      Complete and confirm the extracted information first
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent
              value={INTAKE_TAB_NAMES.PUSH_TO_EMR}
              className='flex-1 overflow-hidden'
            >
              {canAccessCreateEpisode() ? (
                <CreateEpisodeStep
                  onComplete={async () => {
                    await loadIntakeDetails(intakeId!);
                  }}
                  onStatusChange={async () => {
                    await loadIntakeDetails(intakeId!);
                  }}
                  onScrollToField={handleScrollToField}
                  onNavigateToTab={handleTabChange}
                  documentNameDict={documentNameDict}
                />
              ) : (
                <Card className='h-full flex items-center justify-center'>
                  <CardContent className='text-center'>
                    <CheckCircle className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                    <p className='text-sm font-medium text-gray-900 mb-1'>
                      Confirmation Required
                    </p>
                    <p className='text-xs text-gray-500'>
                      Complete and confirm the extracted information first
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Assignee Dialog */}
      <IntakeUserAssignmentDialog
        open={isAssigneeDialogOpen}
        onOpenChange={setIsAssigneeDialogOpen}
        intakes={selectedIntake ? [selectedIntake] : []}
        onSuccess={async () => {
          await loadIntakeDetails(intakeId!);
        }}
      />
    </div>
  );
};

export default Intake3;
